import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { QuestionInputField } from '/index';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import getApplicationCommuteWorkforceSelector from '/business/shared/state/selectors/application/getApplicationCommuteWorkforceSelector';
import EmployeeCommutesWorkforceInfoTip from '/business/shared/components/questions/scope3/employeeCommutes/EmployeeCommutesWorkforceInfoTip';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledFormLabel from '/shared/styled/form/StyledFormLabel';
import StyledFormLabelSubtext from '/shared/styled/form/StyledFormLabelSubtext';
import useGetDefaultDistanceUnit, { type DefaultDistanceUnit } from '/business/shared/hooks/useGetDefaultDistanceUnit';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
} from '/business/shared/components/questions/helper/fieldEnums';
import useGetSharedEmployeeCommuteValues, { type CommuteFactor } from './useGetSharedEmployeeCommuteValues';
type UseEmployeeCommutesEstimateSchemaProps = {
  commuterFactors?: CommuteFactor[];
  isEditingItem?: boolean;
  question: QuestionTreeItem;
};
type UseEmployeeCommutesEstimateSchemaResult = {
  pageSchema: QuestionInputField[];
  sidebar: ReactNode;
  formDisabled: boolean;
};
export default function useEmployeeCommutesEstimateSchema({
  commuterFactors,
  isEditingItem = false,
  question,
}: UseEmployeeCommutesEstimateSchemaProps): UseEmployeeCommutesEstimateSchemaResult {
  const { t } = useTranslation();
  const [valueDistanceUnit, setValueDistanceUnit] = useState<DefaultDistanceUnit | null>(null);
  const { distanceUnit } = useGetDefaultDistanceUnit();
  useEffect(() => {
    if (valueDistanceUnit === null) {
      setValueDistanceUnit(distanceUnit);
    }
  }, [distanceUnit, valueDistanceUnit]);
  const { hasCommuteWorkforce } = useSelector(getApplicationCommuteWorkforceSelector);
  const { commuterFactorValues, distanceUnitValues, distanceValues, employmentTypeField } =
    useGetSharedEmployeeCommuteValues({
      commuterFactors,
      valueDistanceUnit,
      commuterFactorDefaultI18nKey: 'business:QuestionPage.forms.commutesEstimates.selectTransportMode',
      distanceDefaultI18nKey: 'business:QuestionPage.forms.commutesEstimates.selectDistance',
    });
  const commuterFactor: QuestionInputField = useMemo(() => {
    return {
      propName: 'commuterFactor',
      type: FIELD_TYPE_DROPDOWN,
      element: 'commuter_factor',
      labelI18nKey: 'business:QuestionPage.forms.commutesEstimates.para3',
      values: commuterFactorValues,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOCOMMUTERFACTOR',
          errStr: t('business:Errors.transport.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRCOMMUTERFACTORNOTFOUND',
          errStr: t('business:Errors.transport.notFound'),
        },
      ],
    };
  }, [commuterFactorValues, t]);
  const distance: QuestionInputField = useMemo(() => {
    return {
      propName: 'distance',
      type: FIELD_TYPE_DROPDOWN,
      values: distanceValues,
      element: 'distance',
      // the label copy carries its own asterisk; suppress the auto one
      titleHasAsterisk: false,
      label: (
        <Trans
          i18nKey="business:QuestionPage.forms.commutesEstimates.para4"
          components={{
            1: <StyledFormLabelSubtext $noMargin>{null}</StyledFormLabelSubtext>,
          }}
        />
      ),
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNODISTANCE',
          errStr: t('business:Errors.transport.provideDistance'),
        },
      ],
      dropdown: {
        propName: 'distanceUnit',
        element: 'distance_unit',
        defaultValue: distanceUnit,
        values: distanceUnitValues,
        onChangeCallback: (value) => {
          if (value === 'km' || value === 'miles') {
            setValueDistanceUnit(value);
          }
        },
      },
    };
  }, [distanceUnitValues, distanceUnit, distanceValues, t]);
  const numOfEmployees: QuestionInputField = useMemo(() => {
    return {
      propName: 'numOfEmployees',
      type: FIELD_TYPE_NUMBER,
      inputMode: 'decimal',
      element: 'num_of_employees',
      labelI18nKey: 'business:QuestionPage.forms.commutesEstimates.para5',
      placeholderI18nKey: '0',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNONUMOFEMPLOYEES',
          errStr: t('business:Errors.transport.provideEmployeeNumber'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRELECTRICTOOLOWRENEW',
          errStr: t('business:Errors.fuels.electricRenewTooLow'),
          value: 1,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRELECTRICTOOHIGHRENEW',
          errStr: t('business:Errors.fuels.electricRenewTooHigh'),
          value: 999999,
        },
      ],
      styleInputWidth: '6rem',
    };
  }, [t]);
  const embedFields: QuestionInputField = useMemo(() => {
    return {
      reactElement: (
        <>
          {!isEditingItem && (
            <>
              <StyledFormLabel $moreBottomPadding as="div">
                <StyledHeading as="div" $type="h2">
                  {/* @i18n: For each mode of transport, enter in the number of employees who travel similar distances. */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesEstimates.para1" />
                </StyledHeading>
              </StyledFormLabel>
              <StyledInfoFlash>
                <div>
                  {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesEstimates.infoFlash" />
                </div>
              </StyledInfoFlash>
              <StyledInfoFlash $info>
                <div>
                  {/* @i18n: If an employee commutes in a company vehicle, do not enter the data here. Enter it in[...] */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesEstimates.para2" />
                </div>
              </StyledInfoFlash>
            </>
          )}
        </>
      ),
    };
  }, [isEditingItem]);
  const pageSchema: QuestionInputField[] = useMemo(() => {
    const fields = [embedFields, employmentTypeField, commuterFactor, distance, numOfEmployees];
    if (hasCommuteWorkforce) {
      return fields;
    }
    return fields.map((field) => ({ ...field, isDisabled: true }));
  }, [hasCommuteWorkforce, embedFields, employmentTypeField, commuterFactor, distance, numOfEmployees]);
  return {
    pageSchema,
    sidebar: isEditingItem ? null : <EmployeeCommutesWorkforceInfoTip question={question} />,
    formDisabled: !hasCommuteWorkforce,
  };
}
